import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Alert,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { processLatexString } from '../LatexRenderer';
import SectionWrapper from './SectionWrapper';
import { reviewThemeExtensions } from '../../theme/reviewTheme';

const AnswerPreview = ({ 
  templated,
  edited,
  currentQuestion,
  documentId,
  imageMap = {},
  selectedMcqAnswer,
  onMcqAnswerSelect
}) => {
  const theme = useTheme();

  if (!templated) return null;

  const renderExtendedWithParts = () => (
    <Box>
      {templated.parts.map((part) => (
        <Box key={part.id} sx={{ mb: 2 }}>
          <Typography variant="body2" color="primary">
            Part {part.part_number} Answer:
          </Typography>
          <Typography variant="body1" sx={{ ml: 2, whiteSpace: 'pre-wrap' }}>
            {processLatexString(edited[`part_${part.id}_answer`] || part.part_answer, parseInt(documentId), imageMap)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ({part.part_marks} mark{part.part_marks !== 1 ? 's' : ''})
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderMcqAnswer = () => (
    <Box>
      {/* MCQ Options Display */}
      <Box sx={{ mb: 3 }}>
        <Typography>A) {processLatexString(edited.option_a || templated.option_a, parseInt(documentId), imageMap)}</Typography>
        <Typography>B) {processLatexString(edited.option_b || templated.option_b, parseInt(documentId), imageMap)}</Typography>
        <Typography>C) {processLatexString(edited.option_c || templated.option_c, parseInt(documentId), imageMap)}</Typography>
        <Typography>D) {processLatexString(edited.option_d || templated.option_d, parseInt(documentId), imageMap)}</Typography>
      </Box>
      
      {/* Answer Selection */}
      {currentQuestion.answer?.answer_type === 'CANNOT_ANSWER' && (
        <Alert 
          severity="warning" 
          icon={<WarningIcon />}
          sx={{ mb: 2 }}
        >
          AI could not determine the answer. Please select the correct option.
        </Alert>
      )}
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Select the correct answer:
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {['A', 'B', 'C', 'D'].map(option => {
          const isSelected = selectedMcqAnswer === option;
          const isVerified = currentQuestion.verification_status?.is_fully_verified;
          
          return (
            <Grid item xs={6} key={option}>
              <Paper
                variant={isSelected ? "elevation" : "outlined"}
                onClick={() => !isVerified && onMcqAnswerSelect(option)}
                sx={{
                  ...reviewThemeExtensions.components.mcqOptionCard(theme, isSelected),
                  cursor: isVerified ? 'not-allowed' : 'pointer',
                  opacity: isVerified ? 0.6 : 1,
                  '&:hover': isVerified ? {} : reviewThemeExtensions.components.mcqOptionCard(theme, isSelected)['&:hover']
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isSelected ? (
                    <RadioButtonCheckedIcon color="primary" />
                  ) : (
                    <RadioButtonUncheckedIcon />
                  )}
                  <Typography variant="h6" component="span">
                    {option}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {selectedMcqAnswer && currentQuestion.answer?.answer_type !== 'CANNOT_ANSWER' && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="textSecondary">
            AI Answer: {currentQuestion.answer?.ai_answer || 'None'}
          </Typography>
          {currentQuestion.answer?.human_answer && (
            <Typography variant="body2" color="primary">
              | Human Override: {currentQuestion.answer?.human_answer}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const renderContent = () => {
    if (templated.type === 'mcq') {
      return renderMcqAnswer();
    }

    if (templated.type === 'extended') {
      if (templated.has_parts && templated.parts && templated.parts.length > 0) {
        return renderExtendedWithParts();
      }
      
      return (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
          {processLatexString(edited.solution || templated.solution, parseInt(documentId), imageMap)}
        </Typography>
      );
    }

    return null;
  };

  return (
    <SectionWrapper title="Answer Preview">
      <Box sx={{ overflow: 'auto', maxWidth: '100%', flex: 1 }}>
        {renderContent()}
      </Box>
    </SectionWrapper>
  );
};

export default AnswerPreview;